import type { LocaleDictionary } from "@/i18n/types";

const nsVersionEN = {
  fallbackName: "Version",
  unknownTitle: "Unknown Version",
  list: {
    showLatestOnly: ({ count }: { count: number }) =>
      `Show latest ${count} only`,
    showAll: "Show All",
    pageTitle: "Version List",
    column: {
      name: "Version Name",
      status: "Status",
      download: "Download",
      category: "Category",
      publishedAt: "Published At",
      updatedAt: "Last Updated",
      viewers: "Viewers",
    },
    filterByName: "Filter by Version Name",
    editTitleAriaLabel: "Edit Title",
    resetFilter: "Reset Filters",
    tabEnabled: "Active Versions",
    tabDisabled: "Deleted Versions",
    disabledTabNote: "Versions will be permanently deleted after 30 days.",
  },
  notFoundError: "Version not found.",
  removeDialog: {
    title: "Delete Version Permanently",
    message: ({ versionName }: { versionName: string }) =>
      `"${versionName}" will be permanently deleted.`,
    warning: "This action cannot be undone.",
    button: "Delete Permanently",
    inProgressButton: "Permanently deleting...",
    success: "Version permanently deleted.",
    error: "Failed to permanently delete version.",
  },
  removeSomeDialog: {
    title: "Delete Selected Versions Permanently",
    message: "Permanently delete selected versions?",
    warning: "This action cannot be undone.",
    startButton: "Delete Permanently",
  },
  rename: {
    success: "Version name changed.",
    error: "Failed to rename version.",
  },
  operation: {
    optium: {
      create: "Created",
      upload: "File Upload",
      evaluate: "Evaluation",
      optimize: "Optimization",
      file_optimize: "File Optimization",
      file_evaluate: "File Evaluation",
    },
    planium: {
      create: "Created",
      upload: "File Upload",
      evaluate: "Evaluation",
      optimize: "Auto Draft",
      file_optimize: "File Optimization",
      file_evaluate: "File Evaluation",
    },
  },
  status: {
    initial: "Initial",
    optium: {
      queued: "Queued",
      queued_optimization: "Queued for Optimization",
      in_progress_evaluation: "Evaluating",
      in_progress_optimization: "Optimizing",
      success_evaluation: "Evaluation Succeeded",
      success_optimization: "Optimization Succeeded",
      failure_in_optimization: "Optimization Failed",
      failure_input_evaluation: "Validation Failed",
      failure_exception: "Error",
      canceled_evaluation: "Evaluation Canceled",
      canceled_optimization: "Optimization Canceled",
    },
    planium: {
      queued: "Queued",
      queued_optimization: "Queued for Auto Draft",
      in_progress_evaluation: "Evaluating",
      in_progress_optimization: "Creating Auto Draft",
      success_evaluation: "Evaluation Succeeded",
      success_optimization: "Auto Draft Succeeded",
      failure_in_optimization: "Auto Draft Failed",
      failure_input_evaluation: "Validation Failed",
      failure_exception: "Error",
      canceled_evaluation: "Evaluation Canceled",
      canceled_optimization: "Auto Draft Canceled",
    },
  },
  download: {
    inputButton: "Input",
    resultButton: "Result",
    errorButton: "Error",
    inputSuccess: "Input downloaded.",
    inputError: "Failed to download input.",
    resultSuccess: "Result downloaded.",
    resultError: "Failed to download result.",
    resultNotFoundError: "File not found.",
    resultServerError: "A server error occurred.",
    errorDetailSuccess: "Error details downloaded.",
    errorDetailError: "Failed to download error details.",
    multiMenuItem: "Download Inputs / Results",
    multiInputs: ({ count, max }: { count: number; max: number }) =>
      `Download ${count} Input(s) (Max ${max})`,
    multiResults: ({ count, max }: { count: number; max: number }) =>
      `Download ${count} Result(s) (Max ${max})`,
    multiInputsSuccess: "Inputs for selected versions downloaded.",
    multiInputsError: "Failed to download inputs for selected versions.",
    multiResultsSuccess: "Results for selected versions downloaded.",
    multiResultsError: "Failed to download results for selected versions.",
  },
  toolbar: {
    duplicate: "Duplicate",
    delete: "Delete",
    moreActions: "More Actions",
    restore: "Restore",
    remove: "Delete Permanently",
    updateViewSetting: "Update View Settings",
    updateSheetSetting: "Update Sheet Settings",
    selectTargetHint: "Select versions",
  },
  create: {
    newVersion: "Create New Version",
    newVersionFromConfigFile: "Create Version from Config File",
  },
  category: {
    uncategorized: "Uncategorized",
    filterPlaceholder: "Filter by Category",
    create: "Create Category",
    rename: "Rename Category",
    sort: "Reorder Category",
    delete: "Delete Category",
    notFoundError: "Category not found.",
    deleteDialog: {
      title: "Delete Category",
      message: ({ categoryName }: { categoryName: string }) =>
        `Delete ${categoryName}? Associated versions will be moved to Uncategorized.`,
      messageWithoutName:
        "Delete this category? Associated versions will be moved to Uncategorized.",
      button: "Delete",
      inProgressButton: "Deleting...",
    },
    deleteSuccess: "Category deleted.",
    deleteError: "Failed to delete category.",
    deleteSortError:
      "Category deleted, but failed to update the display order.",
  },
  priority: {
    label: "Priority Version",
    setMenuItem: "Set as Priority Version",
    unsetMenuItem: "Remove Priority Version",
    setDialog: {
      title: "Set as Priority Version",
      message: ({
        categoryName,
        versionTitle,
      }: {
        categoryName: string;
        versionTitle: string;
      }) =>
        `Set "${versionTitle}" as the priority version for "${categoryName}"?`,
      currentPriorityNote: ({ versionTitle }: { versionTitle: string }) =>
        `This will replace the current priority version, "${versionTitle}".`,
      button: "Set",
    },
    unsetDialog: {
      title: "Remove Priority Version",
      message: ({
        categoryName,
        versionTitle,
      }: {
        categoryName: string;
        versionTitle: string;
      }) =>
        `Remove "${versionTitle}" as the priority version for ${categoryName}?`,
      button: "Remove",
    },
    updateSuccess: "Priority version updated.",
    updateError: "Failed to update priority version.",
    changedByOtherUserError: ({ versionTitle }: { versionTitle: string }) =>
      `Another user has updated the priority version to "${versionTitle}". Please reload the page.`,
    clearedByOtherUserError:
      "Another user has removed the priority version. Please reload the page.",
  },
  actionsMenu: {
    ariaLabel: "Version Menu",
  },
  row: {
    openViewAndSheetList: "Open View & Sheet List",
    versionInfo: "View/Edit Version Details",
    rename: "Rename Version",
    copyName: "Copy Version Name",
    duplicate: "Duplicate Version",
    delete: "Delete Version",
  },
  copyName: {
    success: "Version name copied.",
    error: "Failed to copy version name.",
  },
  bulkUpdate: {
    targetName: {
      sheetSchema: "sheet settings",
      viewSetting: "view settings",
    },
    dialogTitle: ({ targetName }: { targetName: string }) =>
      `Update ${targetName}`,
    button: "Update All",
    description: ({
      count,
      targetName,
    }: {
      count: number;
      targetName: string;
    }) => `This will update ${targetName} for ${count} selected version(s).`,
    selectFile: "Select a YAML file for the update.",
    optionsLabel: "Update Options",
    statusListTitle: "Update Status",
    allowDestructive: {
      sheetSchema:
        "Allow changes that delete or rename sheets and sheet schemas",
      viewSetting: "Allow changes that delete views",
    },
    replaceConfirm: {
      title: {
        sheetSchema: "Confirm Destructive Changes",
        viewSetting: "Confirm Update with Deletions",
      },
      message: {
        sheetSchema:
          "Differences in the YAML file may cause existing sheets and sheet schemas to be deleted or renamed.",
        viewSetting: "Views not included in the YAML file will be deleted.",
      },
      button: {
        sheetSchema: "Allow and Continue",
        viewSetting: "Allow Deletions and Continue",
      },
    },
  },
  updateCategory: {
    clearPriorityError: "Failed to clear priority version.",
    clearPriorityRollbackError: ({
      clearError,
      rollbackError,
    }: {
      clearError: string;
      rollbackError: string;
    }) =>
      `Failed to clear priority version and rollback also failed. Data may be in an inconsistent state.\nClear error: ${clearError}\nRollback error: ${rollbackError}`,
    success: ({ count }: { count: number }) =>
      count === 1
        ? "Category updated for 1 version."
        : `Category updated for ${count} versions.`,
    error: ({ count }: { count: number }) =>
      count === 1
        ? "Failed to update category for 1 version."
        : `Failed to update category for ${count} versions.`,
    menuItem: "Change Category",
    groupHeader: "Select New Category",
  },
  referencedSheetsCheckError: "Failed to check sheet references.",
  duplicate: {
    inProgress: ({ count }: { count: number }) =>
      count === 1
        ? "Duplicating version..."
        : `Duplicating ${count} versions...`,
    success: ({ count }: { count: number }) =>
      count === 1 ? "Version duplicated." : `${count} versions duplicated.`,
    error: ({ count }: { count: number }) =>
      count === 1
        ? "Failed to duplicate version."
        : `Failed to duplicate ${count} versions.`,
    notCopyableError: ({ versionId }: { versionId: string }) =>
      `Version ID ${versionId} cannot be duplicated.`,
    dialog: {
      title: "Duplicate Version",
      message: ({ versionName }: { versionName: string }) =>
        `Enter a name for the new copy of "${versionName}".`,
      button: "Duplicate",
    },
  },
  restore: {
    inProgress: ({ count }: { count: number }) =>
      count === 1 ? "Restoring version..." : `Restoring ${count} versions...`,
    success: ({ count }: { count: number }) =>
      count === 1 ? "Version restored." : `${count} versions restored.`,
    error: ({ count }: { count: number }) =>
      count === 1
        ? "Failed to restore version."
        : `Failed to restore ${count} versions.`,
  },
  delete: {
    success: ({ count }: { count: number }) =>
      count === 1 ? "Version deleted." : `${count} versions deleted.`,
    error: ({ count }: { count: number }) =>
      count === 1
        ? "Failed to delete version."
        : `Failed to delete ${count} versions.`,
  },
  deletedBand: {
    title: "This version has been deleted",
    message:
      "It will be permanently deleted automatically 30 days after deletion.",
  },
  deleteDialog: {
    title: "Delete Version",
    message: ({ versionName }: { versionName: string }) =>
      `"${versionName}" will be permanently deleted in 30 days. You can restore it anytime before then from the deleted versions list.`,
    messageSome: ({ count }: { count: number }) =>
      `${count} versions will be permanently deleted in 30 days. You can restore them anytime before then from the deleted versions list.`,
    referencedSheetsWarning:
      "This includes sheets referenced by other sheets, which may cause display issues.",
    button: "Delete",
    inProgressButton: "Deleting...",
    error: "Failed to delete version.",
  },
  cancel: {
    target: {
      optium: "Evaluation/Optimization",
      planium: "Auto Draft",
    },
    menuItem: ({ target }: { target: string }) => `Stop ${target}`,
    dialog: {
      title: ({ target }: { target: string }) => `Stop ${target}`,
      message: ({
        target,
        versionName,
      }: {
        target: string;
        versionName: string;
      }) => `Stop ${target} for version "${versionName}"?`,
      messageWithoutName: ({ target }: { target: string }) => `Stop ${target}?`,
      warning: "This action cannot be undone or resumed.",
      button: "Stop",
      cancelButton: "Keep Running",
    },
    success: ({
      target,
      versionName,
    }: {
      target: string;
      versionName: string;
    }) => `Stopped ${target} for version "${versionName}".`,
    error: ({ target, versionName }: { target: string; versionName: string }) =>
      `Failed to stop ${target} for version "${versionName}".`,
  },
  fileOptimize: {
    menuButton: "File Optimization",
    dialog: {
      title: "File Optimization",
      description:
        "Run evaluation / optimization on the file to create a new version.",
      button: "Run",
      evaluateOnly: "Run Evaluation Only",
      versionTitle: "Version Title",
      versionComment: "Version Comment",
      category: "Category for New Version",
      algorithmVersion: "Algorithm Version",
      noAlgorithmVersion:
        "No algorithm version available. Please create one first.",
      noAlgorithmVersionPermission:
        "You do not have permission to view algorithm versions.",
      algorithmVersionId: "Algorithm Version ID (Auto-filled)",
    },
    startSuccess: "File optimization started. This may take a few moments.",
    error: "Failed to run file optimization.",
  },
  comparison: {
    pageTitle: "Version Summary Comparison",
    description: {
      compare: "Select a base version to compare numerical changes.",
      sortHint: "Click a column header to sort the list.",
      download: "Version summary list.",
      downloadHint:
        "Download summaries as a CSV file. Select the versions to download.",
    },
    filterLabel: "Filter by Version Name, Category, or Summary Content",
    searchPlaceholder: "Filter by version name, category, or summary",
    baseLabel: ({ name }: { name: string }) => `Use "${name}" as the Base`,
    baseBadge: "Base",
    maxCount: "Max Rows",
    latestCountOption: ({ count }: { count: number }) =>
      `Latest ${count} Versions`,
    column: {
      versionTitle: "Version Name",
      updatedAt: "Last Updated",
      category: "Category",
      alertCount: "Alerts",
      errorCount: "Errors",
    },
    unit: {
      // 1 件でも複数形にならない単位を持てないので en では単位を出さない
      count: "",
    },
    download: {
      enterMode: "Switch to Download Mode",
      submit: "Download CSV",
      selectionBarLabel: "Actions for Selected Versions",
      backToCompare: "Back to Comparison Mode",
      clearSelection: ({ count }: { count: number }) =>
        `Deselect ${count} Version(s)`,
      failure: "Failed to download summaries.",
      csv: {
        summaryName: ({ level }: { level: number }) =>
          `Summary Name (${level})`,
        unit: "Unit",
      },
    },
    loadError: "Failed to load data.",
  },
} satisfies LocaleDictionary;

export default nsVersionEN;
